'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Image, Text, IconButton, Icon, Link } from '@skin-seoul/design-system';
import {
  formatCurrency,
  type Currency,
  type CartItemResponse,
  CLASSNAME,
  productSizeAndPackTextConverter,
} from '@skin-seoul/service';
import { debounce, isNil, isNotNil } from 'es-toolkit';
import { useDidUpdate, Condition } from '@skin-seoul/utils';
import CartNumberInput from './CartNumberInput';
import CartProductCardSkeleton from './CartProductCardSkeleton';

const CART_ITEM_CARD_TEST_ID = 'cart-item-card';
const REMOVE_ITEM_FROM_CART_TEST_ID = 'remove-item-from-cart';
const CART_ITEM_NUMBER_INPUT_TEST_ID = 'cart-item-number-input';

interface CartProductCardProps {
  item: CartItemResponse;
  onQuantityChange: (quantity: number) => void;
  onRemoveItem: () => void;
  onImageClick?: (productLink: string) => void;
  currency: Currency;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
}

const CartProductCard = ({
  item,
  currency,
  onRemoveItem,
  onQuantityChange,
  ImageComponent = ({ src, alt, width, height }) => <img src={src} alt={alt} width={width} height={height} />,
}: CartProductCardProps) => {
  const productSizeAndPackText = productSizeAndPackTextConverter({
    size: item.itemData?.size,
    pack: item.itemData?.pack,
  });

  const [itemCounter, setItemCounter] = React.useState<number | undefined>(item.quantity);
  const [value, setValue] = useState<number>(item.quantity);
  const prevValue = useRef<number>(item.quantity);

  const itemMax = 9999;
  const itemMin = item.quantityLimit?.min ?? 1;

  const debouncedQuantityUpdate = useMemo(
    () =>
      debounce(
        (quantity: number) => {
          if (quantity === item.quantity) {
            return;
          }
          if (isNil(quantity) || isNaN(quantity)) {
            return;
          }
          if (quantity < itemMin) {
            return onQuantityChange(itemMin);
          }
          if (quantity > itemMax) {
            return onQuantityChange(itemMax);
          }
          onQuantityChange(quantity);
        },
        1400,
        {
          edges: ['trailing'],
        },
      ),
    [item.quantity, itemMin, itemMax, onQuantityChange],
  );

  const handleQuantityChange = (valueAsNumber: number) => {
    debouncedQuantityUpdate(valueAsNumber);
  };

  const handleRemoveItem = () => {
    onRemoveItem();
  };

  useDidUpdate(() => {
    setItemCounter(item.quantity);

    // Logic to solve the issue where the Drawer opens before the server state is updated
    if (prevValue.current !== item.quantity) {
      setValue(item.quantity);
      prevValue.current = item.quantity;
    }
  }, [item.quantity]);

  useDidUpdate(() => {
    if (isNotNil(itemCounter)) {
      handleQuantityChange(itemCounter);
    }
  }, [itemCounter]);

  useEffect(() => {
    return () => {
      debouncedQuantityUpdate.cancel();
    };
  }, [debouncedQuantityUpdate]);

  return (
    <Flex
      width="100%"
      gap={3}
      position="relative"
      paddingTop="27px"
      paddingX="0"
      paddingBottom="24px"
      data-testid={CART_ITEM_CARD_TEST_ID}
    >
      <Link
        unstyled
        href={item.productLink}
        alignItems="center"
        justifyContent="center"
        minWidth="112px"
        width="112px"
        height="112px"
        border="1px solid"
        borderColor="neutral.100"
        borderRadius="8px"
        padding={1}
        cursor="pointer"
      >
        <Image asChild alt={item.name}>
          <ImageComponent src={item.image[0].thumbnail} alt={item.name} width={100} height={100} />
        </Image>
      </Link>

      <Flex direction="column" width="100%" justifyContent="space-between">
        <Flex direction="column" justifyContent="space-between" width="100%" marginBottom={3}>
          <Flex direction="row" gap={1} alignItems="center" justifyContent="space-between">
            <Text textStyle="paragraph.medium" lineClamp={1} className={CLASSNAME.NOT_TRANSLATE}>
              {item.name}
            </Text>
            <IconButton
              size="2xs"
              variant="ghost"
              colorPalette="neutral"
              onClick={handleRemoveItem}
              aria-label="Remove item"
              data-testid={REMOVE_ITEM_FROM_CART_TEST_ID}
            >
              <Icon width="24px" height="24px">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="white" stroke="black" strokeWidth="4" />
                  <path d="M35 35 L65 65 M65 35 L35 65" stroke="black" strokeWidth="5" strokeLinecap="round" />
                </svg>
              </Icon>
            </IconButton>
          </Flex>
          <Condition
            expression={Boolean(productSizeAndPackText)}
            then={
              <Text textStyle="label.small" color="neutral.600" className={CLASSNAME.NOT_TRANSLATE}>
                {productSizeAndPackText}
              </Text>
            }
          />
        </Flex>

        <Flex justify="space-between" align="center">
          <CartNumberInput
            data-testid={CART_ITEM_NUMBER_INPUT_TEST_ID}
            min={1}
            value={isNaN(value) ? '' : String(value)}
            defaultValue={String(item.quantity)}
            max={itemMax}
            onValueChange={({ valueAsNumber }) => {
              setValue(valueAsNumber);
              setItemCounter(valueAsNumber);
            }}
          />
          <Text textStyle="paragraph.medium" className={CLASSNAME.NOT_TRANSLATE}>
            {formatCurrency(currency, item.price.salePrice)}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

CartProductCard.Skeleton = CartProductCardSkeleton;

export default CartProductCard;
